/**
 * terrain-overlays.ts — v4M13.3 — per-kind procedural overlay vocabulary,
 * render-tier map, and parallax-depth helper for terrain features.
 *
 * Layered on top of the v4M13.1 sprite-base-or-fallback render in
 * `system-view.ts:drawEnvironmentEntities`. The base draws the static
 * sprite (or procedural fallback); this module adds the per-tick
 * animation overlay that gives each terrain kind its motion identity.
 *
 * Authoritative spec: `TERRAIN_FEATURES.md §2.2` (overlay vocabulary
 * per kind), `§2.3` (parallax depth tiers), `§2.5` (render order).
 *
 * Animation source: `state.tick` only at v4M13.3. Sim cadence is 10 Hz,
 * so all overlays look slightly "steppy" at 60 fps render rate.
 * `performance.now()`-driven smooth animation is a v4M13 follow-up if
 * playtest finds the tick cadence too choppy.
 *
 * All overlay functions are pure draws (no state mutation, no allocations
 * beyond what canvas demands). Deterministic given `(env.id, tick)`.
 */

// Camera is owned by `ui/state` and re-used by render modules (including
// `./camera`), but `./camera` doesn't re-export the type. Import directly
// from the source to avoid TS2305 ("has no exported member").
import type { Camera } from '../ui/state';
import type { EnvironmentEntity, EnvironmentKind } from '../state/types/index';

// ---------------------------------------------------------------------------
// Render tier + parallax — TERRAIN_FEATURES.md §2.3 + §2.5
// ---------------------------------------------------------------------------

/**
 * Render-order tier per kind. `drawEnvironmentEntities` walks entities
 * in three passes: background first (drawn behind everything else),
 * midground next, foreground last (drawn over the midground but still
 * under ships/structures). Within a tier, ascending `entity.id` is the
 * tiebreak for determinism.
 */
export const ENVIRONMENT_RENDER_TIER: Record<
  EnvironmentKind,
  'background' | 'midground' | 'foreground'
> = {
  nebula: 'background',
  'radiation-belt': 'background',
  'asteroid-field': 'midground',
  'exotic-gas-cloud': 'midground',
  'ion-cloud': 'midground',
  'black-hole': 'foreground',
};

export const RENDER_TIER_ORDER: ReadonlyArray<'background' | 'midground' | 'foreground'> = [
  'background',
  'midground',
  'foreground',
];

/**
 * Per-kind parallax depth factor. 1.0 = moves with the camera at the
 * standard rate (midground / foreground default). < 1.0 = moves slower
 * than the camera pan, suggesting "further away" (background tier).
 * Subtle by design — 5% off feels like depth without becoming jarring
 * during fast pans.
 */
export const ENVIRONMENT_PARALLAX_FACTOR: Record<EnvironmentKind, number> = {
  nebula: 0.95,
  'radiation-belt': 0.95,
  'asteroid-field': 1.0,
  'exotic-gas-cloud': 1.0,
  'ion-cloud': 1.0,
  'black-hole': 1.0,
};

/**
 * World-space position shifted by the parallax factor for the entity's
 * kind. Pass the result through the standard `worldToScreen` —
 * downstream projection math doesn't need to know parallax exists.
 *
 * Math: standard `worldToScreen` computes `(world - cameraCentre) ×
 * zoom`. For parallax factor `d`, we want `(world - cameraCentre × d) ×
 * zoom`. Rearranging: pass `world + cameraCentre × (1 - d)` through
 * the standard formula. When `d = 1` (midground / foreground), the
 * shift is zero and behaviour matches the un-parallaxed case.
 */
export function parallaxedWorldPos(
  env: EnvironmentEntity,
  camera: Camera,
): { x: number; y: number } {
  const factor = ENVIRONMENT_PARALLAX_FACTOR[env.kind];
  const shift = 1 - factor;
  return {
    x: env.sx + camera.centerX * shift,
    y: env.sy + camera.centerY * shift,
  };
}

// ---------------------------------------------------------------------------
// Seeded random — small inline hash for deterministic overlay placement
// ---------------------------------------------------------------------------

/**
 * Deterministic float in [0, 1) from two integer inputs. Inline hash
 * (no allocation, no module deps) so overlay functions can scatter
 * particles / arc endpoints without depending on `state.rng` (which is
 * sim-side state — overlays are pure visuals and must not consume RNG).
 */
function seededRand(seed: number, i: number): number {
  let h = (seed * 374761393 + i * 668265263) | 0;
  h = ((h ^ (h >>> 13)) * 1274126177) | 0;
  return ((h ^ (h >>> 16)) >>> 0) / 0xffffffff;
}

// ---------------------------------------------------------------------------
// Per-kind overlay implementations — TERRAIN_FEATURES.md §2.2
// ---------------------------------------------------------------------------

/**
 * Per-tick rotating asteroid-point particles around the sprite centre.
 * Conveys "this is moving rock" without sprite-frame cycles.
 */
function overlayAsteroidField(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  tick: number,
  seed: number,
): void {
  const numParticles = 8;
  // Slow shared rotation drives the orbital sweep; each particle
  // sits at its own seeded radius so the cluster doesn't look like
  // a perfect ring.
  const baseAngle = tick * 0.005;
  ctx.fillStyle = 'rgba(200, 170, 140, 0.6)';
  for (let i = 0; i < numParticles; i++) {
    const angle = baseAngle + (i / numParticles) * Math.PI * 2;
    const particleR = r * (0.3 + 0.6 * seededRand(seed, i));
    const px = cx + Math.cos(angle) * particleR;
    const py = cy + Math.sin(angle) * particleR;
    ctx.beginPath();
    ctx.arc(px, py, 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Subtle radial pulse — soft halo ring at `r × (1 ± 5%)` scale,
 * driven by a slow sine. Conveys "this is alive / active" without
 * disturbing the static sprite read.
 *
 * v4M13.4a renamed the slot key from `'gas-giant'` to
 * `'exotic-gas-cloud'` alongside the EnvironmentKind type-union edit.
 * The function name preserves the visual function — a slow halo pulse
 * — under the post-rename type name.
 */
function overlayExoticGasCloud(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  tick: number,
): void {
  const pulse = 1 + 0.05 * Math.sin(tick * 0.05);
  ctx.strokeStyle = 'rgba(180, 100, 220, 0.25)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx, cy, r * pulse, 0, Math.PI * 2);
  ctx.stroke();
}

/**
 * 4 bright drift particles each traversing their own axis through
 * the nebula. Suggests interstellar dust motion.
 */
function overlayNebula(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  tick: number,
  seed: number,
): void {
  const numParticles = 4;
  ctx.fillStyle = 'rgba(255, 240, 200, 0.6)';
  for (let i = 0; i < numParticles; i++) {
    const angle = seededRand(seed, i) * Math.PI * 2;
    const speed = 0.002 + seededRand(seed, i + 100) * 0.002;
    // Phase 0..1 over the traversal cycle; map to -0.8r..0.8r along axis.
    const phase = (tick * speed + i / numParticles) % 1;
    const dist = (phase * 2 - 1) * r * 0.8;
    const px = cx + Math.cos(angle) * dist;
    const py = cy + Math.sin(angle) * dist;
    ctx.beginPath();
    ctx.arc(px, py, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Rotating accretion-disc segments + spiralling infall particles.
 * The disc segments rotate as a rigid set (suggests orbital sweep);
 * the infall particles spiral inward and fade as they approach the
 * singularity (suggests gravitational capture).
 */
function overlayBlackHole(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  tick: number,
): void {
  // Accretion disc — 8 arc segments rotating as a rigid set.
  const discRotation = tick * 0.02;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(discRotation);
  ctx.strokeStyle = 'rgba(255, 180, 80, 0.4)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 8; i++) {
    const segAngle = (i / 8) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.65, segAngle - 0.1, segAngle + 0.1);
    ctx.stroke();
  }
  ctx.restore();
  // Infall particles — spiral inward, fade as they approach.
  const numParticles = 4;
  for (let i = 0; i < numParticles; i++) {
    const phase = (tick * 0.01 + i / numParticles) % 1;
    const infallR = r * 0.7 * (1 - phase);
    const infallAngle = phase * Math.PI * 4 + (i / numParticles) * Math.PI * 2;
    const px = cx + Math.cos(infallAngle) * infallR;
    const py = cy + Math.sin(infallAngle) * infallR;
    const alpha = 0.7 * (1 - phase);
    ctx.fillStyle = `rgba(255, 200, 100, ${alpha})`;
    ctx.beginPath();
    ctx.arc(px, py, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Crackling electric arcs between random pairs of points within the
 * cloud. The arc seed advances every 10 ticks (1 second of sim time)
 * so the arcs visibly redraw rather than appearing as continuous
 * smooth shapes. Each arc has a perturbed midpoint for jagged
 * lightning feel.
 */
function overlayIonCloud(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  tick: number,
  seed: number,
): void {
  const arcSeed = seed + Math.floor(tick / 10);
  const numArcs = 3;
  ctx.strokeStyle = 'rgba(200, 240, 255, 0.7)';
  ctx.lineWidth = 1.5;
  for (let i = 0; i < numArcs; i++) {
    const a1 = seededRand(arcSeed, i * 4) * Math.PI * 2;
    const r1 = seededRand(arcSeed, i * 4 + 1) * r * 0.8;
    const a2 = seededRand(arcSeed, i * 4 + 2) * Math.PI * 2;
    const r2 = seededRand(arcSeed, i * 4 + 3) * r * 0.8;
    const x1 = cx + Math.cos(a1) * r1;
    const y1 = cy + Math.sin(a1) * r1;
    const x2 = cx + Math.cos(a2) * r2;
    const y2 = cy + Math.sin(a2) * r2;
    // Perturbed midpoint creates the jagged lightning shape.
    const mx = (x1 + x2) / 2 + (seededRand(arcSeed, i * 4 + 100) - 0.5) * 8;
    const my = (y1 + y2) / 2 + (seededRand(arcSeed, i * 4 + 101) - 0.5) * 8;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(mx, my);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
}

/**
 * Three concentric warning rings, alpha pulsing on a 60-tick cycle
 * (6 seconds at 10 Hz sim cadence). Conveys "danger gradient" —
 * field intensity radiating from inner to outer ring.
 */
function overlayRadiationBelt(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  tick: number,
): void {
  const phase = (tick % 60) / 60;
  const alpha = 0.3 + 0.3 * Math.sin(phase * Math.PI * 2);
  ctx.strokeStyle = `rgba(255, 240, 100, ${alpha})`;
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 3; i++) {
    const ringR = r * (0.4 + i * 0.25);
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
    ctx.stroke();
  }
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

/**
 * Draw the per-kind procedural overlay for an environment entity.
 * Called by `system-view.ts:drawEnvironmentEntities` AFTER the sprite
 * or procedural-fallback base, so the overlay always lays on top.
 *
 * Pure dispatch — picks the right per-kind function and forwards the
 * draw context + position + tick. No state mutation.
 */
export function drawTerrainOverlay(
  ctx: CanvasRenderingContext2D,
  env: EnvironmentEntity,
  cx: number,
  cy: number,
  r: number,
  tick: number,
): void {
  switch (env.kind) {
    case 'asteroid-field':
      overlayAsteroidField(ctx, cx, cy, r, tick, env.id);
      return;
    case 'exotic-gas-cloud':
      overlayExoticGasCloud(ctx, cx, cy, r, tick);
      return;
    case 'nebula':
      overlayNebula(ctx, cx, cy, r, tick, env.id);
      return;
    case 'black-hole':
      overlayBlackHole(ctx, cx, cy, r, tick);
      return;
    case 'ion-cloud':
      overlayIonCloud(ctx, cx, cy, r, tick, env.id);
      return;
    case 'radiation-belt':
      overlayRadiationBelt(ctx, cx, cy, r, tick);
      return;
  }
}
